import {remCalc} from '../_utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

type SideCardProps = {
    backgroundColor?: string
    hasPaddings?: boolean
    isPdpPage?: boolean
    isFull?: boolean
    isReversed?: boolean
    isSmallTablet?: boolean
    hasDetails?: boolean
    isExpanded?: boolean
    layoutType?: 'horizontal' | 'vertical'
}

const overrides = {
    Header: {
        baseStyle: {
            headerIcon: {
                color: 'background.dark',
                '@media (min-width: 1200px)': {
                    width: remCalc(24),
                    height: remCalc(24),
                },
            },
            hideOnMobile: {
                '@media (max-width: 1199px)': {
                    display: 'none',
                },
                '@media (min-width: 1200px)': {
                    display: 'flex',
                },
            },
            hideOnDesktop: {
                '@media (max-width: 1199px)': {
                    display: 'flex',
                    gap: {base: remCalc(32), md: remCalc(24), lg: remCalc(32), xl: remCalc(24)},
                },
                '@media (min-width: 1200px)': {
                    display: 'none',
                },
            },
            showOnXl: {
                '@media (max-width: 1399px)': {
                    display: 'none',
                },
                '@media (min-width: 1400px)': {
                    display: 'flex',
                },
            },
            showOnLgOnly: {
                '@media (max-width: 1199px)': {
                    display: 'none',
                },
            },
            bodyContainerGap: {
                '@media (min-width: 1200px)': {
                    gap: remCalc(80),
                },
            },
            wishlistIcon: {
                '@media (min-width: 1200px)': {
                    boxSize: 'iconsSize.medium',
                    fill: 'background.default',
                    color: 'background.dark',
                    strokewidthnumber: remCalc(3),
                },
            },
            logo: {
                '@media (max-width: 1023px)': {
                    width: remCalc(136.19),
                    height: remCalc(20),
                },
                '@media (min-width: 1024px)': {
                    width: remCalc(143),
                    height: remCalc(21),
                },
            },
            containerSpacing: {
                '@media (max-width: 375px)': {
                    paddingY: 'small.base',
                    mx: 0,
                    px: 'small.base',
                },
                '@media (min-width: 376px) and (max-width: 1023px)': {
                    paddingY: 'small.base',
                    mx: 0,
                    px: 'small.lg',
                },
                '@media (min-width: 1024px)': {
                    paddingY: remCalc(24),
                    mx: 'auto',
                },
            },
            iconsFlexGap: {
                gap: {base: remCalc(16), md: remCalc(16), lg: remCalc(16)},
                '@media (min-width: 1199px)': {
                    gap: remCalc(16),
                },
            },
            accountIcon: {
                '@media (min-width: 1200px)': {
                    width: remCalc(24),
                    height: remCalc(24),
                },
            },
        },
    },
    Footer: {
        baseStyle: {
            outerContainer: {
                background: 'background.light',
            },
            container: {
                background: 'background.light',
            },
            benefitText: {
                color: 'text.default',
                textStyle: 'head-xxsmall',
                fontWeight: 'semibold',
            },
            benefitIcons: {
                color: 'background.dark',
                boxSize: remCalc(48),
            },
            policies: {
                color: 'text.default',
                opacity: 1,
            },
            copyright: {
                color: 'text.secondary',
                mt: 0,
            },
            socials: {
                color: 'background.dark',
                boxSize: ['iconsSize.medium'],
            },
            socialIcons: {
                color: 'background.dark',
            },
            accordionList: {
                color: 'text.default',
            },
            accordionItem: {
                borderColor: 'background.grey',
            },
            accordionHeading: {
                color: 'text.default',
                textStyle: ['head-xxsmall'],
                fontWeight: 600,
            },
            accordionIcon: {
                color: 'border.dark',
                boxSize: 'iconsSize.medium',
            },
            dividerRule: {
                borderStyle: 'none',
            },
            kenwoodDividerRule: {
                borderStyle: 'solid',
            },
            kenwoodCopyrightBrand: {
                flexDirection: {base: 'column', lg: 'row'},
                alignItems: {base: 'center', lg: 'center'},
                gap: {base: remCalc(16), lg: remCalc(24)},
            },
            mobileStack: {
                paddingY: {base: remCalc(16), md: remCalc(24)},
            },
            iconsBenefitSection: {
                paddingInline: 0,
            },
            benefitStack: {
                minHeight: {base: '100%', lg: remCalc(120)},
            },
            linkColumnContainer: {
                columnGap: remCalc(24),
            },
            footerOuterContainer: {
                bg: 'background.light',
            },
            footerBrand: {
                width: remCalc(150),
                height: remCalc(22.5),
            },
            copyBox: {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
            },
            copyPositioning: {
                position: 'relative',
                width: 'auto',
                bottom: 0,
                right: 0,
                textAlign: 'right',
            },
            paymentsLegal: {
                alignItems: 'center',
            },
            teconsent: {
                textStyle: ['body-xsmall'],
            },
            mobileLogoCoutry: {
                flexDirection: 'row',
            },
        },
        variants: {
            checkout: {
                container: {
                    pt: {base: remCalc(0), lg: remCalc(16), xl: remCalc(30)},
                    pb: {base: remCalc(0), lg: remCalc(16), xl: remCalc(30)},
                    paddingInline: {
                        base: remCalc(16),
                        md: remCalc(16),
                        lg: remCalc(16),
                        '2xl': remCalc(0),
                    },
                },
            },
        },
    },
    SwatchGroup: {
        baseStyle: {
            swatchGroup: {
                textStyle: 'body-small',
            },
            swatchLabel: {
                alignItems: 'baseline',
            },
        },
    },
    InformativeLabel: {
        variants: {
            light: {
                color: 'text.primary',
                backgroundColor: 'label.background.secondary',
            },
        },
    },
    LinksList: {
        baseStyle: {
            containerLinks: {
                color: 'text.default',
                opacity: 1,
            },
            heading: {
                color: 'text.default',
                textStyle: 'head-xxsmall',
                textTransform: 'uppercase',
                pb: remCalc(24),
                fontWeight: 600,
            },
            headingLink: {
                color: 'text.default',
                textStyle: 'body-xsmall',
                fontWeight: 400,
                opacity: 1,
            },
            list: {
                textStyle: ['body-xsmall'],
                opacity: 1,
                color: 'text.default',
                gap: 'small.base',
            },
        },
    },
    SovraHeader: {
        baseStyle: {
            container: {
                background: 'background.dark',
            },
            icon: {
                color: 'border.secondary',
                opacity: 1,
                height: remCalc(24),
            },
            label: {
                fontWeight: 'semibold',
            },
            labelSmall: {
                textStyle: 'body-small',
                fontWeight: 'semibold',
            },
            hstack: {
                gap: remCalc(24),
            },
            containerDesktop: {
                h: 'auto',
                paddingY: remCalc(8),
            },
            labelColor: {
                fontWeight: 'semibold',
            },
        },
    },
    PreFooter: {
        baseStyle: {
            benefitBarHeader: {
                textStyle: 'head-xsmall',
                fontWeight: 600,
            },
            benefitBarStack: {
                flex: '1 0 0 ',
            },
            newsletterContainer: {
                background: 'background.light-grey',
            },
            newsletterStack: {
                gap: remCalc(8),
            },
            newsletterHeader: {
                color: 'text.default',
                textStyle: 'head-large',
                fontWeight: 600,
            },
            newsletterText: {
                textStyle: 'body-xsmall',
                color: 'text.default',
                fontWeight: 400,
            },
            newsletterButton: {
                variant: 'secondary',
                textStyle: 'body-xsmall',
                textTransform: 'uppercase',
                maxW: remCalc(230),
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
            },
            benefitBarIcons: {
                boxSize: remCalc(48),
            },
            benefitBarBox: {
                gap: remCalc(8),
            },
            benefitBarContainer: {
                marginY: {lg: remCalc(16)},
            },
            newsletterFlex: {
                columnGap: {lg: remCalc(80)},
                gap: {base: remCalc(16), lg: remCalc(24)},
            },
        },
    },
    CountrySelectorFooter: {
        baseStyle: {
            openButton: {
                justifyContent: 'flex-start',
                _before: {
                    content: '""',
                    position: 'absolute',
                    bg: 'transparent',
                    borderRadius: 0,
                },
                svg: {
                    color: 'text.default',
                },
            },
            countrySvgContainer: {
                width: remCalc(24),
                height: remCalc(18),
                borderRadius: 0,
            },
            buttonText: {
                color: 'text.default',
            },
            buttonArrow: {
                color: 'text.default',
            },
            websiteIcon: {
                color: 'text.default',
            },
        },
    },
    PageHeader: {
        baseStyle: {
            title: {
                color: 'text.default',
            },
        },
    },
    ContentCards: {
        baseStyle: {
            subtitle: {
                opacity: '0.7',
            },
        },
    },
    KeyFeatures: {
        baseStyle: {
            headerGrid: {
                color: 'text.default',
            },
            cardTitle: {
                color: 'text.default',
            },
            cardDescription: {
                color: 'text.default',
            },
        },
    },
    StockLabel: {
        baseStyle: {
            icon: {
                borderRadius: 'none',
            },
        },
        variants: {
            outOfStock: {
                icon: {
                    fill: 'text.danger',
                    backgroundColor: 'text.danger',
                },
                text: {color: 'text.danger'},
            },
            lastFewItems: {
                icon: {fill: 'text.violet', backgroundColor: 'text.violet'},
                text: {color: 'text.violet'},
            },
        },
    },
    ProductComparePlp: {
        baseStyle: {
            bannerRemoveItems: {color: 'button.text.icon-default !important'},
            compareTableHeaderLabel: {color: 'text.default'},
        },
    },
    Klarna: {
        baseStyle: {
            klarnaPlacement: {
                '#klarna-placement::part(osm-cta)': {
                    textStyle: 'body-small',
                    color: 'text.primary',
                    fontWeight: 'bold',
                },
            },
        },
    },
    PromoSlider: {
        baseStyle: {
            container: {
                backgroundColor: 'background.light',
            },
            contentContainer: {
                backgroundColor: 'background.default',
                alignItems: 'flex-start',
            },
            chevronIcon: {
                color: 'background.alternative',
                backgroundColor: 'transparent',
                border: 'none',
            },
            textTitleDescription: {
                textAlign: 'left',
                color: 'text.default',
                childrenTextColor: 'text.default',
            },
            childrenTextColor: 'text.default',
            promoSliderCardButton: {
                variant: 'primary',
            },
            promoSlideCardText: {
                color: 'unset',
            },
            imageCardChevronIcon: {
                backgroundColor: 'transparent',
                borderRadius: 'none',
                color: 'text.inverse',
            },
        },
    },
    HeroBannerText: {
        baseStyle: {
            textTitle: {textStyle: 'body-medium', fontWeight: 'normal'},
            textTitleDescription: {textStyle: 'display-small', fontWeight: 'bold'},
        },
    },
    Moodboard: {
        baseStyle: {
            moodboardButton: {
                variant: 'primary',
            },
            chevronIcon: {
                display: 'none',
            },
        },
    },
    Video: {
        baseStyle: {
            playIcon: {
                color: 'background.alternative',
            },
        },
    },
    Account: {
        baseStyle: () => {
            const hidePassword = getAssetUrl('static/img/form/hide-password-kw.svg')
            const showPassword = getAssetUrl('static/img/form/show-password-kw.svg')

            return {
                '.gigya-screen .gigya-composite-control-password-peek input[type=password] + .gigya-show-password-button .gigya-password-icon, .gigya-screen .gigya-composite-control-password-peek[data-show-password=false] .gigya-password-icon':
                    {
                        content: `url("${hidePassword}")`,
                    },
                '.gigya-screen .gigya-composite-control-password-peek input[type=text] + .gigya-show-password-button .gigya-password-icon, .gigya-screen .gigya-composite-control-password-peek[data-show-password=true] .gigya-password-icon':
                    {
                        content: `url("${showPassword}")`,
                    },
                greetingsContainer: {
                    borderRadius: 0,
                },
                sideBarSection: {
                    borderRadius: 0,
                },
                productRegistrationBox: {
                    borderRadius: 0,
                },
                '.gigya-input-submit': {
                    _hover: {
                        color: 'button.text.primary-hover',
                        border: remCalc(2),
                        borderColor: 'button.border.primary-hover',
                        borderStyle: 'solid',
                    },
                },
            }
        },
    },
    Accordion: {
        baseStyle: {
            checkIconBox: {
                color: 'background.dark',
            },
        },
    },
    HeroBannerCountdown: {
        baseStyle: {
            text: {textStyle: 'body-small', fontWeight: 'normal'},
            timeNumber: {
                textStyle: 'head-xlarge',
            },
        },
    },
    Bundle: {
        baseStyle: {
            productsContainer: {
                gap: {base: 0, lg: remCalc(8)},
            },
            addCartButtonContainer: {
                pb: {base: remCalc(16), lg: 0},
                marginTop: {base: remCalc(16), lg: remCalc(40)},
            },
            divider: {
                display: {base: 'block', lg: 'none'},
            },
            subtotalPrice: {
                marginBottom: {base: remCalc(16), lg: 0},
            },
            plusIconContainer: {
                display: 'none',
            },
            plusIconContainerMobile: {
                display: 'flex',
                marginRight: {base: remCalc(16), lg: remCalc(8)},
                marginLeft: remCalc(16),
                padding: {base: 0, lg: remCalc(8)},
            },
            bundleCard: {
                flexDir: {base: 'row-reverse', lg: 'column-reverse'},
                w: {base: remCalc(327), lg: remCalc(291)},
                h: {base: 'auto', lg: remCalc(344)},
            },
            firstBundleCard: {
                w: {base: remCalc(327), lg: remCalc(342.5)},
                h: {base: 'auto', lg: 'auto'},
            },
            productDetailName: {
                marginBottom: {base: remCalc(0), lg: remCalc(16)},
            },
            bundleDiscountLabel: {
                bottom: {base: remCalc(84), lg: remCalc(95)},
            },
            productTileImage: {
                height: {base: 'auto', lg: '100%'},
            },
        },
    },
    ContactUs: {
        baseStyle: {
            button: {
                borderRadius: 0,
            },
            rightSection: {
                p: {
                    color: 'text.default',
                },
                ul: {
                    color: 'text.secondary',
                },
                li: {
                    color: 'text.secondary',
                },
                a: {
                    color: 'text.tertiary',
                    textDecoration: 'underline',
                },
                b: {
                    color: 'text.tertiary',
                },
                em: {
                    textStyle: 'body-xsmall',
                    color: 'text.secondary',
                },
            },
        },
    },
    KenwoodMenu: {
        baseStyle: {
            listMenuTriggerContainer: {
                textStyle: 'head-xxsmall',
                color: 'text.default',
            },
        },
    },
    ProductCompare: {
        baseStyle: {
            specsButton: {
                color: 'text.primary',
            },
            gridLabel: {
                fontWeight: 'bold',
            },
            headerText: {
                textStyle: 'head-xsmall',
            },
        },
    },
    ProductTile: {
        variants: {
            bundle: {
                image: {
                    ratio: {base: 85 / 95, lg: 4 / 3},
                },
                firstBundleImage: {
                    ratio: {base: 85 / 95, lg: 17 / 16},
                },
                imageWrapper: {
                    paddingX: {base: 0, lg: remCalc(16)},
                    paddingBottom: {base: 0, lg: remCalc(16)},
                },
            },
        },
    },
    Confirmation: {
        baseStyle: {
            continueShoppingButton: {
                _hover: {
                    bg: 'button.background.primary-hover',
                    color: 'button.background.primary-default',
                    border: remCalc(1),
                    borderStyle: 'solid',
                    textDecoration: 'none',
                },
            },
            checkIconBox: {
                backgroundColor: 'transparent',
                border: 'none',
                alignSelf: 'center',
                boxSize: 'iconsSize.xlarge',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            },
            checkIcon: {
                color: 'text.success',
            },
        },
    },
    ProductRegistrationConfirm: {
        baseStyle: {
            checkIconBox: {
                backgroundColor: 'transparent',
                border: 'none',
                alignSelf: 'center',
                boxSize: 'iconsSize.xlarge',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            },
            checkIcon: {
                color: 'text.success',
            },
        },
    },
    ProductRegistrationForm: {
        baseStyle: {
            registrationFormSubtitle: {
                textStyle: 'body-xsmall',
            },
            registrationFormSubtitleDescription: {
                textStyle: 'body-xxsmall',
            },
        },
    },
    Button: {
        variants: {
            primary: {
                _hover: {
                    bg: 'button.background.primary-hover',
                    color: 'button.text.primary-hover',
                    border: remCalc(1),
                    borderColor: 'button.background.primary-default',
                    borderStyle: 'solid',
                },
            },
            'primary-small': {
                _hover: {
                    bg: 'button.background.primary-hover',
                    color: 'button.text.primary-hover',
                    border: remCalc(1),
                    borderColor: 'button.background.primary-default',
                    borderStyle: 'solid',
                },
            },
            secondary: {
                _hover: {
                    background: 'button.background.inverse-primary-hover',
                    color: 'button.text.inverse-primary-hover',
                },
            },
            'secondary-small': {
                _hover: {
                    background: 'button.background.inverse-primary-hover',
                    color: 'button.text.inverse-primary-hover',
                },
            },
            'inverted-pressed': {
                bg: 'button.background.inverse-secondary-pressed',
                color: 'button.text.inverse-secondary-pressed',
                borderColor: 'button.border.inverse-secondary-pressed',
            },
        },
    },
    Modal: {
        baseStyle: {
            productButton: {
                _hover: {
                    bg: 'button.background.primary-hover',
                    color: 'button.background.primary-default',
                    border: remCalc(1),
                    borderStyle: 'solid',
                    textDecoration: 'none',
                },
            },
        },
    },
    MiniCartPopover: {
        baseStyle: {
            cartIcon: {
                '@media (min-width: 1200px)': {
                    width: remCalc(24),
                    height: remCalc(24),
                },
            },
            popoverContainer: {
                borderRadius: 0,
            },
        },
    },
    WhatsInTheBox: {
        baseStyle: {
            headerLabel: {
                fontWeight: 'bold',
                mb: 0,
                pt: {base: remCalc(24), lg: remCalc(40)},
                pb: {base: remCalc(24), lg: remCalc(40)},
            },
        },
        variants: {
            greyBackgroundVariant: {
                outerContainer: {
                    pt: {base: remCalc(16), lg: remCalc(40)},
                    pb: {base: remCalc(16), lg: remCalc(40)},
                },
            },
        },
    },
    Sorting: {
        baseStyle: {
            chevronIcon: {
                border: 'none',
                color: 'text.primary',
            },
        },
    },
    SideCard: {
        baseStyle: (props: SideCardProps) => ({
            verticalOutterContent: {
                mx:
                    props.isSmallTablet && !props.hasDetails
                        ? remCalc(85)
                        : props.hasDetails
                        ? remCalc(16)
                        : {base: remCalc(16), md: remCalc(107)},
            },
        }),
    },
    Wishlist: {
        baseStyle: {
            wishlistTitle: {
                paddingBottom: {base: remCalc(24), lg: 0},
                paddingTop: remCalc(24),
            },
            wishlistLogin: {
                gap: {base: remCalc(18), lg: remCalc(24)},
            },
            wishlistBackButton: {
                textStyle: 'body-xsmall',
            },
            wishlistTextSuggestion: {
                maxWidth: remCalc(451),
            },
        },
        variants: {
            'empty-wishlist': {
                heading: {
                    fontWeight: 'bold',
                },
                textContainer: {
                    gap: remCalc(16),
                },
            },
        },
    },
    Login: {
        baseStyle: () => {
            const hidePassword = getAssetUrl('static/img/form/hide-password-kw.svg')
            const showPassword = getAssetUrl('static/img/form/show-password-kw.svg')

            return {
                '.gigya-screen .gigya-composite-control-password-peek input[type=password] + .gigya-show-password-button .gigya-password-icon, .gigya-screen .gigya-composite-control-password-peek[data-show-password=false] .gigya-password-icon':
                    {
                        content: `url("${hidePassword}")`,
                    },
                '.gigya-screen .gigya-composite-control-password-peek input[type=text] + .gigya-show-password-button .gigya-password-icon, .gigya-screen .gigya-composite-control-password-peek[data-show-password=true] .gigya-password-icon':
                    {
                        content: `url("${showPassword}")`,
                    },
                '.gigya-input-submit': {
                    _hover: {
                        bg: 'button.background.primary-hover',
                        color: 'button.background.primary-default',
                        border: remCalc(1),
                        borderColor: 'button.background.primary-default',
                        borderStyle: 'solid',
                    },
                },
                '[data-screenset-element-id=gigya-login-popup-screen] .dln-btn': {
                    _hover: {
                        bg: 'button.background.primary-hover',
                        color: 'button.background.primary-default',
                        border: remCalc(1),
                        borderColor: 'button.background.primary-default',
                        borderStyle: 'solid',
                    },
                },
                checkIconBox: {
                    backgroundColor: 'transparent',
                    border: 'none',
                    alignSelf: 'center',
                    boxSize: 'iconsSize.xlarge',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                },
                checkIcon: {
                    color: 'text.success',
                },
            }
        },
    },
    Benefits: {
        baseStyle: {
            icon: {
                boxSize: 'iconsSize.large',
            },
        },
    },
    BenefitCard: {
        baseStyle: {
            infoIcon: {
                color: 'button.text.secondary-default',
                boxSize: 'iconsSize.medium',
            },
        },
    },

    WeeeFee: {
        baseStyle: {
            infoIcon: {
                color: 'icons.background.tertiary',
            },
        },
    },
    CountrySelector: {
        baseStyle: {
            closeIcon: {
                color: 'background.dark',
                stroke: 'none',
            },
            content: {
                height: ['auto', 'auto', 'auto', remCalc(80)],
                gap: [remCalc(16), remCalc(16), remCalc(16), remCalc(24)],
            },
            blueButton: {
                width: ['100%', '100%', '100%', 'fit-content'],
                minWidth: remCalc(140),
                textTransform: 'uppercase',
            },
            whiteButton: {
                width: ['100%', '100%', '100%', 'fit-content'],
                minWidth: remCalc(140),
                textTransform: 'uppercase',
                bg: 'button.background.inverse-primary-default',
            },
        },
    },
    ProductView: {
        baseStyle: {
            inventoryLabelContainer: {
                pt: [remCalc(24), remCalc(24), remCalc(24), remCalc(24)],
            },
        },
    },
    ProductDetailBanner: {
        baseStyle: {
            codeText: {
                alignSelf: 'center',
                position: 'relative',
                top: '1px',
            },
            tagText: {
                top: remCalc(0),
            },
        },
    },
    ProductRegistration: {
        baseStyle: {
            singleCardContainer: {
                borderRadius: 0,
            },
            link: {
                fontWeight: 'semibold',
            },
        },
    },
    Filters: {
        baseStyle: {
            selectedRefinementsButton: {
                borderColor: 'border.secondary',
            },
        },
    },
    InfoPopover: {
        baseStyle: {
            iconBox: {
                maxHeight: 'unset',
            },
        },
    },
    PriceCard: {
        baseStyle: {
            infoIcon: {
                boxSize: remCalc(24),
                color: 'button.text.secondary-default',
            },
        },
    },
    CartSecondaryButtonGroup: {
        baseStyle: {
            deleteIcon: {verticalAlign: 'top'},
        },
    },
    Cart: {
        baseStyle: {
            shipping: {
                shippingIcon: {
                    boxSize: {base: 'iconsSize.medium', lg: 'iconsSize.large'},
                    color: 'background.dark',
                },
                closeIcon: {
                    boxSize: {base: 'iconsSize.medium', lg: 'iconsSize.large'},
                    color: 'background.dark',
                },
            },
        },
    },
    QuantityPicker: {
        baseStyle: {
            decButton: {
                bg: 'background.off-white',
                color: 'text.default',
                border: remCalc(1),
                borderColor: 'border.light',
                borderRight: 0,
            },
        },
    },
    OrderSummary: {
        baseStyle: {
            checkoutSection: {
                goToCheckoutButtonContainerSticky: {
                    paddingBottom: 'xsmall.base',
                },
            },
            promoCode: {
                inputProps: {
                    borderRadius: 0,
                },
            },
        },
    },
    Orders: {
        baseStyle: {
            widgetWrapper: {
                borderRadius: 0,
            },
            wrapper: {borderRadius: 0},
            goBackToOrderHistory: {
                fontWeight: 600,
            },
            header: {
                color: 'text.default',
            },
            orderHistoryHeader: {
                color: 'text.default',
            },
            stackWrapper: {
                borderRadius: 0,
            },
            borderWrapper: {
                borderRadius: 0,
            },
            orderReturnHeaderNumDateContainer: {
                borderRadius: 0,
            },
            returnFormWarningContainer: {
                borderRadius: 0,
            },
            expiredReturnContainer: {
                borderRadius: 0,
            },
            expiredNoReturnContainer: {
                borderRadius: 0,
            },
            returnOrderSelect: {
                borderRadius: 0,
            },
            returnModalContainer: {
                borderRadius: 0,
                px: [remCalc(16), remCalc(16), remCalc(24), remCalc(24)],
                paddingTop: [remCalc(24), remCalc(24), remCalc(24), remCalc(40)],
                paddingBottom: [remCalc(24), remCalc(24), remCalc(24), remCalc(40)],
            },
            returnModalButton: {
                pt: ['small.base', 'small.sm', 'small.md', 'small.lg'],
                pb: ['medium.base', 'medium.sm', 'medium.md', 'medium.lg'],
            },
            returnModalStepsContainer: {
                gap: remCalc(16),
                paddingBottom: ['small.base', 'small.sm', 'small.md', 'small.lg'],
            },
            orderDetailsReturnContainer: {
                backgroundColor: 'background.default',
                px: remCalc(0),
            },
        },
    },
    Checkout: {
        baseStyle: {
            toggleIconBox: {
                backgroundColor: 'transparent',
            },
            toggleCheckIcon: {
                color: 'text.success',
            },
        },
        variants: {
            'profile-card': {
                toggleCardBox: {
                    borderRadius: 0,
                    borderColor: {base: 'transparent', lg: 'border.light'},
                },
            },
        },
    },
    OrderHistory: {
        baseStyle: {
            title: {
                textColor: 'text.default',
            },
        },
    },
    MarketingPreferences: {
        baseStyle: {
            accountTitle: {
                textColor: 'text.default',
            },
            '.gigya-composite-control-checkbox .gigya-checkbox-text': {
                color: 'text.default',
            },
        },
    },
    ProductRegistrationProducts: {
        baseStyle: {
            productLabels: {
                gap: {base: remCalc(16)},
            },
            productButton: {
                borderRadius: 0,
            },
        },
    },
    Swiper: {
        baseStyle: {
            '.promo-slider-pagination': {
                paddingTop: remCalc(8),
            },
        },
    },
    ServiceCenterLocator: {
        baseStyle: {
            filterIconContainer: {
                maxHeight: 'fit-content',
                padding: {
                    base: `${remCalc(24)} ${remCalc(16)}`,
                    lg: `${remCalc(8)} ${remCalc(16)}`,
                },
                borderRadius: 0,
            },
            filterPopUpContainer: {
                paddingTop: {base: 0, lg: remCalc(12)},
                paddingBottom: {base: 0, lg: remCalc(10)},
            },
            filterSectionContainerMobile: {
                w: {base: `calc(100% - ${remCalc(32)})`, lg: remCalc(312)},
            },
            radioButton: {
                borderWidth: remCalc(1),
            },
            showMoreResultsButton: {
                variant: 'primary',
                borderRadius: 0,
                borderColor: 'button.background.primary-default',
            },
            mapTooltipContactButton: {
                borderRadius: 0,
                variant: 'primary',
            },
            tooltipGoToMapsButton: {
                borderRadius: 0,
                color: 'button.text.secondary-default',
                borderColor: 'button.border.secondary-default',
                variant: 'secondary',
                _hover: {
                    bg: 'button.background.secondary-hover',
                    color: 'button.text.primary-default',
                    border: remCalc(1),
                    borderStyle: 'solid',
                    textDecoration: 'none',
                },
            },
            hideMapButton: {
                borderRadius: 0,
                color: 'button.text.secondary-default',
                borderColor: 'button.border.secondary-default',
                variant: 'secondary',
            },
            showMapButton: {
                borderRadius: 0,
                color: 'button.text.secondary-default',
                borderColor: 'button.border.secondary-default',
                variant: 'secondary',
            },
            removeAllButton: {
                textDecoration: 'none',
            },
            selectedFilterValue: {
                fontWeight: 'normal',
            },
        },
    },
    Faq: {
        baseStyle: {
            pageContainer: {
                gap: {base: remCalc(24), lg: remCalc(64)},
            },
            searchAndTitleStyles: {
                subTitleText: {
                    paddingTop: {base: remCalc(24), lg: remCalc(40)},
                },
                innerContainer: {
                    padding: `${remCalc(24)} 0`,
                },
            },
            categoriesCardStyles: {
                categoriesCardVStack: {
                    marginTop: {base: 0, lg: remCalc(-24)},
                },
            },
            searchContainerGap: {
                gap: {base: remCalc(24), lg: remCalc(40)},
            },
        },
    },
    Paperless: {
        baseStyle: {
            headingTitle: {
                textStyle: 'head-large',
            },
        },
    },
    PaperlessBanner: {
        baseStyle: {
            bannerTitle: {
                textStyle: 'head-large',
                wordBreak: {base: 'break-all'},
            },
        },
    },
    Disclaimer: {
        baseStyle: {
            heading: {
                fontWeight: 'bold',
            },
            anchor: {
                textStyle: 'body-xsmall',
                color: 'button.text.text-default',
                textDecoration: 'underline',
                _hover: {
                    textDecoration: 'underline',
                },
            },
        },
    },
    ProductMoodboard: {
        baseStyle: {
            content: {
                '.swiper-pagination-bullet-active': {
                    textDecorationThickness: remCalc(2),
                },
                '.swiper-pagination-bullet': {
                    fontWeight: 'medium',
                },
            },
            imageoverlay: {
                backgroundColor: 'rgba(0, 0, 0, 0.035)',
            },
        },
    },
    ContentSlider: {
        baseStyle: {
            pagination: {
                textStyle: 'body-xxsmall',
                fontWeight: 'medium',
                textTransform: 'uppercase',
            },
            textblocktitle: {
                textStyle: 'head-large',
            },
            textblockbodycopy: {
                textStyle: 'body-large',
            },
            chevronIcon: {
                color: 'button.text.primary-default',
                backgroundColor: 'colors.null',
            },
        },
    },
    ShopTheLook: {
        baseStyle: {
            chevronIcon: {
                border: 'none',
            },
        },
    },
    PlpBannerPromo: {
        baseStyle: {
            main: {
                py: {base: 'medium.base', md: 'small.md'},
                gap: {base: remCalc(32), md: 'small.md'},
                alignItems: {lg: 'center'},
            },
            textblock: {
                gap: 'xxsmall.lg',
            },
            heading: {
                fontWeight: 'semibold',
            },
            cta: {
                textDecoration: 'none !important',
                w: remCalc(214),
                _hover: {
                    bg: 'transparent',
                    color: 'button.text.primary-default',
                    border: 'button.border.secondary-default',
                    borderColor: 'button.border.inverse-secondary-default',
                },
            },
        },
    },
}

export default {
    components: overrides,
}
